<template>
  <div class="add-processing">
    <SLoadingOverlay :loading="loading" class="transparent-overlay" />
    <VCard
      variant="flat"
      class="px-2 py-3 card"
      :data-testid="isAvailable ? 'availableProduct' : 'unavailableProduct'"
      style="position:relative;"
    >
      <NuxtLink
        :id="imgLinkId"
        :to="productLink"
        data-testid="product-card-link"
        @click.capture="emits('clicked-product')"
      >
        <ProductBadge
          v-if="showProductBanner"
          :product="product"
          class="ml-2 mt-2"
        />
        <WishlistComponent
          v-if="showWishlist && !isSubscriptionProduct && !isRefillProduct && !isPotmProduct"
          :product="product"
          category-page
          class="wishlist-icon pt-3 pr-2"
          @updating="loading = !loading"
        />
        <div
          :id="`image-${product.id}`"
          class="add-processing"
          @mouseover="mouseover = true"
          @mouseout="mouseover = false"
        >
          <SImage
            v-show="!showHoverImage"
            native-lazy
            :eager-load="eagerImage"
            :defer-load="deferImage"
            :src="imageSrc"
            :srcset="imageSrcSet"
            :sizes="sizes"
            :alt="imageAlt"
            class="img img-lg"
            height="360"
            width="360"
          />
          <SImage
            v-if="hoverImageExist"
            v-show="showHoverImage"
            native-lazy
            :src="hoverSrc"
            :srcset="hoverSrcSet"
            :sizes="hoverSrcSet ? sizes : undefined"
            :alt="imageAlt"
            class="img-lg"
          />
            <VOverlay
              v-model="addedOverlay"
              color="#f8f8f8"
              location="center"
              target="parent"
              transition="fade-transition"
              location-strategy="connected"
              contained
              scrim="backgroundDiv"
              opacity="0.90"
              class="updated-overlay"
            >
              <template v-if="!loading">
                <VIcon size="28" color="successCheck">
                  {{ icons.mdiCheck }}
                </VIcon>
                <span class="font18 font-weight-medium text-primary">{{ $t('label.updatedSuccessfully') }}</span>
              </template>
            </VOverlay>
        </div>
      </NuxtLink>
      <VRow no-gutters>
        <VCol order="1" order-sm="1" cols="12" class="d-flex flex-column" style="min-height: 120px;">
          <VCardText class="px-0 py-2 d-flex justify-space-between" style="line-height:unset;">
            <NuxtLink
              :id="linkId"
              :to="productLink"
              data-testid="product-card-link"
              class="text-left"
              @click.capture="emits('clicked-product')"
            >
              <span
                class="text-uppercase font-weight-medium product-title"
                :class="[{ 'clamp': truncateTitle, [`clamp--${truncateTitle}`]: truncateTitle }]"
                data-testid="product-title"
              >
                {{ product.title }}
              </span>
            </NuxtLink>
          </VCardText>

          <VCardText class="font14 pa-0">
            <VContainer
              grid-list-xs
              class="pa-0"
            >
              <VRow
                v-show="showPrice"
                align-content="center"
                no-gutters
              >
                <VCol cols="12" md="auto" class="d-flex align-center pb-1 pb-md-0">
                  <ProductTag
                    :product-on-sale="isOnSale"
                    :life-cycle-state="lifeCycleState"
                    :category-slugs="product.categorySlugs"
                    :exclusive-to="product.exclusiveTo"
                    :qualifier="product.qualifier"
                  />
                </VCol>
                <VCol cols="12" md="auto">
                  <span v-if="!isFree" class="price">
                    <span
                      :class="{ 'text-secondary': isOnSale }"
                      class="font-weight-medium text-primary product-card-price"
                      data-testid="product-formatted-price"
                    > {{ product.formattedItemPrice }} </span>
                    <span
                      v-if="isOnSale"
                      class="text-greyedOut text-strikethrough ml-1"
                      data-testid="product-original-price"
                    > {{ product.formattedOriginalItemPrice }}
                    </span>
                  </span>
                  <span v-else class="text-secondary text-uppercase font-weight-medium pl-1">
                    {{ $t('label.free') }}
                  </span>
                </VCol>
              </VRow>
              <VRow no-gutters class="mt-0">
                <VCol>
                  <ProductStatus
                    :inventory-status="product.inventoryStatus"
                    text
                    small
                    class="status"
                    color="secondary"
                  />
                </VCol>
              </VRow>
            </VContainer>
          </VCardText>

          <VRow no-gutters>
            <VCol v-if="addOnlyOnProductDetailPage" class="align-content-end">
              <SBtn
                block
                variant="outlined"
                class="view-product-button py-2 font14"
                height="28"
                :style="{ borderRadius: '6px', textTransform: 'none' }"
                data-testid="view-product"
                @click="viewProduct()"
              >
                {{ $t('btn.label.view') }}
              </SBtn>
            </VCol>
            <VCol v-else class="align-content-end">
              <AddToCartButton
                :text="$t('cart.btn.addTo')"
                :max-value="product.qtyLimit"
                :loading="loading"
                :initial-quantity="currentQty"
                @add-to-cart="addToCart"
                @qty-updated="updateCart"
                @remove-from-cart="removeFromCart"
              />
            </VCol>
          </VRow>
        </VCol>
      </VRow>
    </VCard>
  </div>
</template>

<script lang="ts" setup>
import { mdiCheck } from '@mdi/js'
import { ExclusiveTo, LifeCycleState } from '~/types/lineitem'
import { InventoryStatus, type ListedProduct as Product } from '~/types/shop'
import { CARD_SIZES, CARD_SRCSET_WIDTHS } from '~/utils/format/imageSizes'
import ProductStatus from '~/components/base/ProductStatus.vue'
import ProductTag from '~/components/products/ProductTag.vue'
import WishlistComponent from '~/components/categories/WishlistMenu.vue'
import ProductBadge from '~/components/products/ProductBadge.vue'
import AddToCartButton from '~/components/base/AddToCartButton.vue'

interface Props {
 product: Product
 showAddButton?: boolean
 showPrice?: boolean
 showProductBanner?: boolean
 hideWishlist?: boolean
 truncateTitle?: number
 imgLinkId?: string
 linkId?: string
 source?: AddToCartSource
 /**
  * The CSS slot this card fills, for the browser to pick a `srcset` candidate
  * against. Defaults to the category grid `ProductList` renders; the containers
  * whose column counts differ pass their own rather than inherit a string that
  * does not describe them (ADO 62980).
  */
 sizes?: string
 // A card in the grid's first row: its image loads at once (SImage eagerLoad).
 eagerImage?: boolean
 // A card below the first viewport: its image is requested after hydration (SImage deferLoad).
 deferImage?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showAddButton: false,
  showPrice: true,
  showProductBanner: true,
  hideWishlist: false,
  truncateTitle: undefined,
  imgLinkId: undefined,
  linkId: undefined,
  source: undefined,
  sizes: CARD_SIZES,
  eagerImage: false,
  deferImage: false
})

const icons = { mdiCheck }
const addedOverlay = ref(false)
const mouseover = ref(false)
const ADD_TO_CART_OVERLAY_TIMEOUT = 1500

const router = useRouter()
const cartStore = useCartStore()

// Every route into the product detail page reports the same event, so the lists that render
// this card can attribute a click to the product and position they rendered it at.
//
// The links report in the capture phase so the event goes out before the router link's own
// click handler starts navigating away and the card is torn down (nuxt2 AB#64009, ported under
// AB#64473; same as Breadcrumbs).
const emits = defineEmits<{
  (e: 'clicked-product'): void
}>()

// The View control navigates by script rather than through a NuxtLink, so it has to report the
// click itself - otherwise the only product-card route that skips select_item is the one taken
// by every product that cannot be added straight to the cart.
const viewProduct = () => {
  emits('clicked-product')
  router.push(productLink.value)
}

const isAvailable = computed(() => props.product.inventoryStatus?.toLowerCase() === InventoryStatus.normal)
const isSubscriptionProduct = computed(() => props.product.exclusiveTo?.includes(ExclusiveTo.SUBSCRIPTION) ?? false)
const isRefillProduct = computed(() => props.product.exclusiveTo?.includes(ExclusiveTo.REFILL) ?? false)
const isPotmProduct = computed(() => props.product.productOfTheMonth ?? false)
const imageSrc = computed(() => images.getResponsiveSrc('card', props.product.id))
const imageSrcSet = computed(() => images.getSrcSet(props.product.id, CARD_SRCSET_WIDTHS))
const imageAlt = computed(() => props.product.title)
/**
 * `hoverImage` arrives from the API as a full delivery URL, and only its
 * library and filename are used: rebuilding them through the seam puts both
 * images of a card on the same host and transform rules, so a card never pulls
 * from two hosts. The product index writes hover images under
 * `product-attribute-images/`, which otherwise load at their full-size
 * original (AB#64927). A URL under neither library is not something the seam
 * can address, so it passes through untouched rather than carrying a
 * transform its host may not understand.
 */
const hoverKey = computed(() => {
  const hoverImage = props.product.hoverImage
  return hoverImage ? images.getImageKey(hoverImage) : undefined
})

// Falls back to the raw URL rather than re-testing `hoverImage` for absence:
// `hoverKey` is undefined both when there is no hover image and when there is
// one the seam cannot address, and only the second case has a URL to fall back
// to. An absent hover image is already the empty case of that fallback.
const hoverSrc = computed(() => {
  const key = hoverKey.value
  return key
    ? images.getResponsiveSrc('card', key.fileName, key.library)
    : props.product.hoverImage || undefined
})

/**
 * A hover image the seam cannot address gets no candidates: every entry would
 * be the same URL at a different width descriptor, so the browser would pick by
 * size and receive identical pixels regardless.
 */
const hoverSrcSet = computed(() => {
  const key = hoverKey.value
  return key ? images.getSrcSet(key.fileName, CARD_SRCSET_WIDTHS, key.library) : undefined
})
const isOnSale = computed(() => props.product.itemPrice < props.product.originalItemPrice)
const isFree = computed(() => props.product.itemPrice === 0)
const hoverImageExist = computed(() => Boolean(props.product.hoverImage))
const showHoverImage = computed(() => Boolean((mouseover.value && !addedOverlay.value) && hoverImageExist.value))
const lifeCycleState = computed(() => props.product.lifeCycleStates?.[0])
const currentQty = computed(() => cartStore.productQtyInCart(props.product.id))
const isNotOrderable = computed(() => props.product.inventoryStatus?.toLowerCase() === InventoryStatus.notorderable)
const isUnavailable = computed(() => !props.product.canBePurchased)
const addOnlyOnProductDetailPage = computed(() => isNotOrderable.value || isFree.value || isPotmProduct.value || isRefillProduct.value || isSubscriptionProduct.value || isUnavailable.value)

const showWishlist = computed(() => !props.hideWishlist &&
  !(props.product.lifeCycleStates?.some(lifeCycleState => [LifeCycleState.LIMITEDACCESS.toLowerCase(), LifeCycleState.PREORDER.toLowerCase()].includes(lifeCycleState.toLowerCase())) ?? false)
)

const productPath = computed(() => `${PRODUCT_ROOT}/${props.product.slug}`)
const { localizedTo: localizedProductPath } = useLocalizedTo(productPath)
const productLink = computed(() => ({
  path: localizedProductPath.value as string,
  query: (props.product.lifeCycleStates?.length ?? 0) > 0 && !(props.product.lifeCycleStates?.includes('NORMAL') ?? true)
    ? {
        itemId: props.product.id
      }
    : undefined
}))

const addedToCart = () => {
  addedOverlay.value = true
  setTimeout(() => {
    addedOverlay.value = false
  }, ADD_TO_CART_OVERLAY_TIMEOUT)
}

const { loading, processAction } = useAction()
const addToCart = () => processAction(async () => {
  await cartStore.addProductToCart({
    product: props.product,
    quantity: 1,
    source: props.source
  })
})

const removeFromCart = () => processAction(async () => {
  await cartStore.removeCartProduct({ productId: props.product.id })
})

const updateCart = (quantity: number) => processAction(async () => {
  addedOverlay.value = true
  await cartStore.updateCartProductQuantity({
    product: props.product,
    quantity,
    source: props.source,
    isIncrement: quantity > currentQty.value
  })
  addedToCart()
})
</script>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use 'sass:map';

  .transparent-overlay {
    background: rgba(255, 255, 255, 0.85);
  }
  .updated-overlay {
    border-radius: 6px;
  }
  .card {
    position: relative;
    border-radius: 0;
    z-index: auto;
    max-width: 100%;

    :deep(.v-card-text) {
      flex: 0 1 auto;
    }
    a {
      text-decoration: none;
    }

    .wishlist-icon {
      position: absolute;
      top: 8px;
      right: 8px;
      z-index: 1;
    }
    .product-title {
      display: flow-root;
      word-break: normal;
      line-height: normal;
      letter-spacing: 0.0125em;
      font-size: 1rem;
    }
    .product-card-price {
      letter-spacing: 0.1px;
    }
    .view-product-button {
      letter-spacing: 1.25px;
      max-width: none;
    }
    .clamp {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      word-break: normal;
    }
    .clamp--2 {
      -webkit-line-clamp: 2;
    }
    .clamp--3 {
      -webkit-line-clamp: 3;
    }
    .img {
      border: 1px solid #f5f5f5;
      object-fit: contain;
      max-width: 480px;
      height: 100%;
      width: 100%;
      border-radius: 6px;
    }
    .colors {
      padding: 0;
      list-style: none;

      > li {
        display: inline-block;
        line-height: 1;
        height: 13px;
        width: 13px;
        border-radius: 50%;
        margin-right: 4px;
      }
    }
    @media #{map.get(settings.$display-breakpoints, 'xs')} {
      .product-title {
        font-size: 0.86rem;
      }
      a {
        .clamp {
          font-size: 0.86rem;
        }
      }
      .price {
        line-height: 14px;
        font-size: 0.86rem;
      }
    }
  }
</style>
